// Bank Account Service for BookAPT
// Handles merchant bank account setup and payout management

import SimpleStorage from '../utils/simpleStorage';

export interface BankAccount {
  id: string;
  merchantId: string;
  accountHolderName: string;
  accountType: 'checking' | 'savings';
  routingNumber: string;
  accountNumber: string; // Only last 4 digits stored
  bankName: string;
  isVerified: boolean;
  isDefault: boolean;
  createdAt: string;
  lastUsed?: string;
  stripeAccountId?: string;
  stripeBankAccountId?: string;
}

export interface BankAccountSetup {
  accountHolderName: string;
  accountType: 'checking' | 'savings';
  routingNumber: string;
  accountNumber: string;
  confirmAccountNumber: string;
  bankName: string;
  bankAddress: string;
  verificationMethod: 'micro_deposits' | 'instant_verification';
}

export interface PayoutInfo {
  nextPayoutDate: string;
  nextPayoutAmount: number;
  payoutSchedule: 'daily' | 'weekly' | 'monthly';
  lastPayoutDate?: string;
  lastPayoutAmount?: number;
  pendingAmount: number;
  bankAccount: {
    last4: string;
    bankName: string;
    accountType: string;
  };
  totalEarnings: number;
  totalPayouts: number;
}

export interface PayoutHistory {
  id: string;
  amount: number;
  status: 'pending' | 'paid' | 'failed';
  scheduledDate: string;
  paidDate?: string;
  bankAccount: string;
  description: string;
  transactionId?: string;
}

class BankAccountService {
  private readonly BANK_ACCOUNTS_KEY = 'bank_accounts';
  private readonly PAYOUT_INFO_KEY = 'payout_info';
  private readonly PAYOUT_HISTORY_KEY = 'payout_history';

  // Create bank account for merchant
  async createBankAccount(merchantId: string, accountData: BankAccountSetup): Promise<BankAccount | null> {
    try {
      // Validate account data
      if (!this.validateAccountData(accountData)) {
        throw new Error('Invalid account information');
      }

      // Create Stripe account (simulated)
      const stripeAccountId = await this.createStripeAccount(merchantId, accountData);
      
      // Add bank account to Stripe (simulated)
      const stripeBankAccountId = await this.addBankAccountToStripe(stripeAccountId, accountData);

      const bankAccount: BankAccount = {
        id: `ba_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        merchantId,
        accountHolderName: accountData.accountHolderName,
        accountType: accountData.accountType,
        routingNumber: accountData.routingNumber,
        accountNumber: this.maskAccountNumber(accountData.accountNumber),
        bankName: accountData.bankName,
        isVerified: false,
        isDefault: true,
        createdAt: new Date().toISOString(),
        stripeAccountId,
        stripeBankAccountId
      };

      // Save to local storage
      await this.saveBankAccount(bankAccount);

      // Initialize payout info
      await this.initializePayoutInfo(merchantId, bankAccount);

      return bankAccount;
    } catch (error) {
      console.error('Error creating bank account:', error);
      return null;
    }
  }

  // Verify bank account
  async verifyBankAccount(accountId: string, verificationData: {
    microDepositAmount1?: number;
    microDepositAmount2?: number;
  }): Promise<boolean> {
    try {
      const bankAccount = await this.getBankAccount(accountId);
      if (!bankAccount) {
        throw new Error('Bank account not found');
      }

      // Simulate verification process
      const isVerified = await this.verifyWithStripe(bankAccount.stripeBankAccountId!, verificationData);
      
      if (isVerified) {
        bankAccount.isVerified = true;
        await this.updateBankAccount(bankAccount);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error verifying bank account:', error);
      return false;
    }
  }

  // Get bank accounts for merchant
  async getBankAccounts(merchantId: string): Promise<BankAccount[]> {
    try {
      const accountsJson = await SimpleStorage.getItem(this.BANK_ACCOUNTS_KEY);
      const accounts: BankAccount[] = accountsJson ? JSON.parse(accountsJson) : [];
      return accounts.filter(account => account.merchantId === merchantId);
    } catch (error) {
      console.error('Error getting bank accounts:', error);
      return [];
    }
  }

  // Get specific bank account
  async getBankAccount(accountId: string): Promise<BankAccount | null> {
    try {
      const accounts = await this.getAllBankAccounts();
      return accounts.find(account => account.id === accountId) || null;
    } catch (error) {
      console.error('Error getting bank account:', error);
      return null;
    }
  }

  // Get payout information
  async getPayoutInfo(merchantId: string): Promise<PayoutInfo | null> {
    try {
      const payoutInfoJson = await SimpleStorage.getItem(`${this.PAYOUT_INFO_KEY}_${merchantId}`);
      return payoutInfoJson ? JSON.parse(payoutInfoJson) : null;
    } catch (error) {
      console.error('Error getting payout info:', error);
      return null;
    }
  }

  // Get payout history
  async getPayoutHistory(merchantId: string): Promise<PayoutHistory[]> {
    try {
      const historyJson = await SimpleStorage.getItem(`${this.PAYOUT_HISTORY_KEY}_${merchantId}`);
      return historyJson ? JSON.parse(historyJson) : [];
    } catch (error) {
      console.error('Error getting payout history:', error);
      return [];
    }
  }

  // Update payout schedule
  async updatePayoutSchedule(merchantId: string, schedule: 'daily' | 'weekly' | 'monthly'): Promise<boolean> {
    try {
      const payoutInfo = await this.getPayoutInfo(merchantId);
      if (!payoutInfo) {
        throw new Error('Payout info not found');
      }

      payoutInfo.payoutSchedule = schedule;
      await SimpleStorage.setItem(`${this.PAYOUT_INFO_KEY}_${merchantId}`, JSON.stringify(payoutInfo));
      return true;
    } catch (error) {
      console.error('Error updating payout schedule:', error);
      return false;
    }
  }

  // Validate account data
  private validateAccountData(data: BankAccountSetup): boolean {
    // Basic validation
    if (!data.accountHolderName || data.accountHolderName.trim().length < 2) {
      return false;
    }

    if (!data.routingNumber || !/^\d{9}$/.test(data.routingNumber)) {
      return false;
    }

    if (!data.accountNumber || data.accountNumber.length < 4) {
      return false;
    }

    if (data.accountNumber !== data.confirmAccountNumber) {
      return false;
    }

    if (!data.bankName || data.bankName.trim().length < 2) {
      return false;
    }

    return true;
  }

  // Mask account number for security
  private maskAccountNumber(accountNumber: string): string {
    if (accountNumber.length <= 4) {
      return '*'.repeat(accountNumber.length);
    }
    return '*'.repeat(accountNumber.length - 4) + accountNumber.slice(-4);
  }

  // Create Stripe account (simulated)
  private async createStripeAccount(merchantId: string, accountData: BankAccountSetup): Promise<string> {
    // In production, this would create a real Stripe Connect account
    await new Promise(resolve => setTimeout(resolve, 1000));
    return `acct_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Add bank account to Stripe (simulated)
  private async addBankAccountToStripe(accountId: string, accountData: BankAccountSetup): Promise<string> {
    // In production, this would add the bank account to Stripe
    await new Promise(resolve => setTimeout(resolve, 1000));
    return `ba_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Verify with Stripe (simulated)
  private async verifyWithStripe(bankAccountId: string, verificationData: any): Promise<boolean> {
    // In production, this would verify with Stripe
    await new Promise(resolve => setTimeout(resolve, 2000));
    return Math.random() > 0.1; // 90% success rate for demo
  }

  // Save bank account
  private async saveBankAccount(bankAccount: BankAccount): Promise<void> {
    try {
      const accounts = await this.getAllBankAccounts();
      accounts.push(bankAccount);
      await SimpleStorage.setItem(this.BANK_ACCOUNTS_KEY, JSON.stringify(accounts));
    } catch (error) {
      console.error('Error saving bank account:', error);
    }
  }

  // Update bank account
  private async updateBankAccount(bankAccount: BankAccount): Promise<void> {
    try {
      const accounts = await this.getAllBankAccounts();
      const index = accounts.findIndex(account => account.id === bankAccount.id);
      if (index !== -1) {
        accounts[index] = bankAccount;
        await SimpleStorage.setItem(this.BANK_ACCOUNTS_KEY, JSON.stringify(accounts));
      }
    } catch (error) {
      console.error('Error updating bank account:', error);
    }
  }

  // Get all bank accounts
  private async getAllBankAccounts(): Promise<BankAccount[]> {
    try {
      const accountsJson = await SimpleStorage.getItem(this.BANK_ACCOUNTS_KEY);
      return accountsJson ? JSON.parse(accountsJson) : [];
    } catch (error) {
      console.error('Error getting all bank accounts:', error);
      return [];
    }
  }

  // Initialize payout info
  private async initializePayoutInfo(merchantId: string, bankAccount: BankAccount): Promise<void> {
    const payoutInfo: PayoutInfo = {
      nextPayoutDate: this.getNextPayoutDate('weekly'),
      nextPayoutAmount: 0,
      payoutSchedule: 'weekly',
      pendingAmount: 0,
      bankAccount: {
        last4: bankAccount.accountNumber.slice(-4),
        bankName: bankAccount.bankName,
        accountType: bankAccount.accountType
      },
      totalEarnings: 0,
      totalPayouts: 0
    };

    await SimpleStorage.setItem(`${this.PAYOUT_INFO_KEY}_${merchantId}`, JSON.stringify(payoutInfo));
  }

  // Get next payout date
  private getNextPayoutDate(schedule: 'daily' | 'weekly' | 'monthly'): string {
    const now = new Date();
    switch (schedule) {
      case 'daily':
        now.setDate(now.getDate() + 1);
        break;
      case 'weekly':
        now.setDate(now.getDate() + 7);
        break;
      case 'monthly':
        now.setMonth(now.getMonth() + 1);
        break;
    }
    return now.toISOString();
  }

  // Simulate payout processing
  async processPayout(merchantId: string, amount: number): Promise<PayoutHistory | null> {
    try {
      const payout: PayoutHistory = {
        id: `payout_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        amount,
        status: 'pending',
        scheduledDate: new Date().toISOString(),
        bankAccount: '****1234',
        description: `Payout for ${new Date().toLocaleDateString()}`,
        transactionId: `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };

      // Add to history
      const history = await this.getPayoutHistory(merchantId);
      history.unshift(payout);
      await SimpleStorage.setItem(`${this.PAYOUT_HISTORY_KEY}_${merchantId}`, JSON.stringify(history));

      // Simulate processing
      setTimeout(async () => {
        payout.status = 'paid';
        payout.paidDate = new Date().toISOString();
        await SimpleStorage.setItem(`${this.PAYOUT_HISTORY_KEY}_${merchantId}`, JSON.stringify(history));
      }, 5000);

      return payout;
    } catch (error) {
      console.error('Error processing payout:', error);
      return null;
    }
  }
}

export const bankAccountService = new BankAccountService();
export default BankAccountService;



